import type { UseDataControlReturn } from 'hooks/useDataControl';
import type { Column } from './columns';
import { cn } from 'helpers/cn';
import { columnLabelMobile, groupColumnsForMobile, renderColumnValue } from './columns';
import { MobileSortBar } from './SortControls';

type DataTableCardsProps<T> = {
  columns: Column<T>[];
  controller: UseDataControlReturn<T>;
  uniqueKey: keyof T;
  renderExpanded?: (item: T) => React.ReactNode | null;
};

/**
 * The table below `md`: one card per row, driven by the same `columns` array.
 *
 * Eight or nine columns squeezed into a ~350px panel was a horizontal-scroll maze, and
 * the fix has to stay in the shared component rather than in each screen — otherwise
 * every list defines its data twice and the two copies drift.
 *
 * The card is not itself a tap target. Whether a title column navigates is not
 * detectable from a `format` that returns arbitrary JSX, and a card-wide handler over
 * the row's own link, menu and switches would swallow their clicks, so the title's own
 * link or button stays the tap target.
 */
export const DataTableCards = <T,>({ columns, controller, uniqueKey, renderExpanded }: DataTableCardsProps<T>) => {
  const { data } = controller;
  const pageData = data ?? [];
  const { title, meta, actions } = groupColumnsForMobile(columns);

  return (
    <div className="grid gap-2 p-3">
      <MobileSortBar columns={columns} controller={controller} />

      {pageData.map((item: T, idx: number) => (
        <article
          key={String(item[uniqueKey] ?? idx)}
          className="border-line bg-panel2 rounded-card grid gap-2 border p-3"
        >
          {title && (
            <div className="text-foreground min-w-0 text-body font-medium">
              {renderColumnValue(title, item, pageData, idx)}
            </div>
          )}

          {meta.map((column, columnIdx) => {
            const label = columnLabelMobile(column);
            const content = renderColumnValue(column, item, pageData, idx);

            // A column with no label — an avatar, a bare select button — gets the full
            // width rather than an empty label gutter.
            if (!label) {
              return (
                <div key={columnIdx} className="min-w-0">
                  {content}
                </div>
              );
            }

            return (
              <div key={columnIdx} className="flex items-start justify-between gap-3">
                <span className="micro-label shrink-0 pt-0.5">{label}</span>
                <div className="text-foreground min-w-0 text-right text-meta">{content}</div>
              </div>
            );
          })}

          {/*
            The screens size their row controls for a mouse — icon buttons at 32px. The
            footer floors every one of them at a 36px thumb target, rather than each
            screen having to restate its button sizes for one breakpoint.
          */}
          {actions.length > 0 && (
            <div
              className={cn(
                'border-line flex flex-wrap items-center justify-end gap-1 border-t pt-2',
                '[&_button]:min-h-9 [&_button]:min-w-9',
              )}
            >
              {actions.map((column, columnIdx) => (
                <div key={columnIdx}>{renderColumnValue(column, item, pageData, idx)}</div>
              ))}
            </div>
          )}

          {renderExpanded?.(item) ?? null}
        </article>
      ))}
    </div>
  );
};
